import {CurrencyInput, FormControl, FormField, isCurrency} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {MutableRefObject, useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {
  OrderPaymentResponseBody,
  useGetOrderQuery,
  usePatchCheckoutOrderForeignCurrencyPaymentMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {DEFAULT_CURRENCY} from '@dms/shared';

import {suffixTestId, TestIdProps, useDebouncedCallback} from 'shared/web';

import {FormValuesType} from '../types/FormValuesType';

interface ForeignCurrencyFieldsProps extends TestIdProps {
  payment: OrderPaymentResponseBody;
  orderId: string;
  checkoutId: string;
  formApi: UseFormReturn<FormValuesType>;
  control: FormControl<FormValuesType>;
  updateExchangeRate: () => void;
  markExchangeRateEditPending: () => void;
  hasUnsentExchangeRateEditRef: MutableRefObject<boolean>;
  latestDispatchedRequestIdRef: MutableRefObject<number>;
  latestSettledRequestIdRef: MutableRefObject<number>;
  staleSettledExchangeRateRef: MutableRefObject<string | null>;
}

export const ForeignCurrencyFields = (props: ForeignCurrencyFieldsProps) => {
  const [, {isLoading: isPatchingForeignCurrency}] =
    usePatchCheckoutOrderForeignCurrencyPaymentMutation({fixedCacheKey: 'foreignCurrencyPayment'});

  const [amount, exchangeRate, paymentMethod] = useWatch({
    name: ['amount', 'exchangeRate', 'paymentMethod'],
    control: props.control,
  });
  const {data: order} = useGetOrderQuery({
    checkoutId: props.checkoutId,
    orderId: props.orderId,
  });

  const depositCurrency =
    isNotNil(order) && isCurrency(order.totalPrice.withVat.currency)
      ? order.totalPrice.withVat.currency
      : undefined;

  const foreignCurrencyValue = amount / parseFloat(exchangeRate ?? '1');

  const debouncedUpdateExchangeRate = useDebouncedCallback(props.updateExchangeRate, 1000);

  const handleExchangeRateChange = () => {
    props.markExchangeRateEditPending();
    debouncedUpdateExchangeRate();
  };

  useEffect(
    function syncExchangeRateFields() {
      const hasOutstandingExchangeRateRequest =
        props.latestSettledRequestIdRef.current !== props.latestDispatchedRequestIdRef.current;

      if (props.hasUnsentExchangeRateEditRef.current || hasOutstandingExchangeRateRequest) {
        return;
      }

      // A settled request whose own value was already superseded by a newer edit still triggers
      // a cache refetch through invalidatesTags — this effect would otherwise re-apply that same
      // stale value here, undoing the newer edit. Skip it once; a genuinely new value (e.g. from
      // an actual currency change) no longer matches and is applied normally.
      if (
        isNotNil(props.staleSettledExchangeRateRef.current) &&
        props.payment.exchangeRate === props.staleSettledExchangeRateRef.current
      ) {
        return;
      }

      const hasForeignCurrencyPayment = isNotNil(props.payment.exchangeRate);

      match(hasForeignCurrencyPayment)
        .with(true, () => {
          props.formApi.setValue('exchangeRate', props.payment.exchangeRate);
          props.formApi.setValue(
            'exchangeRateAmount',
            props.payment.foreignCurrencyAmount?.amount || null
          );
        })
        .otherwise(() => {
          props.formApi.setValue('exchangeRate', null);
          props.formApi.setValue('exchangeRateAmount', null);
        });
    },
    [props.formApi, props.payment.exchangeRate, props.payment.foreignCurrencyAmount?.amount] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const isPaymentByBankTransfer = paymentMethod === 'BANK_TRANSFER';
  const isCustomerBankAccountIdError = isNotNil(
    props.formApi.getFieldState('customerBankAccountId')?.error
  );

  const shouldDisableExchangeRateEdit = isPaymentByBankTransfer && isCustomerBankAccountIdError;

  return (
    <Show when={order?.foreignCurrencyPayment}>
      <FormField
        control={props.control}
        name="exchangeRate"
        type="currency"
        currency={depositCurrency}
        isDisabled={isPatchingForeignCurrency || shouldDisableExchangeRateEdit}
        onChange={handleExchangeRateChange}
        label={i18n.t('general.labels.exchangeRate')}
        data-testid={suffixTestId('exchangeRate', props)}
      />
      <CurrencyInput
        isDisabled
        value={Number(foreignCurrencyValue.toFixed(2))}
        currency={
          isCurrency(order?.currency) ? order?.currency || DEFAULT_CURRENCY : DEFAULT_CURRENCY
        }
        label={i18n.t('entity.checkout.labels.amountInForeignCurrency')}
        data-testid={suffixTestId('amountInForeignCurrency', props)}
      />
    </Show>
  );
};
